<script>
import axios from "axios";
import API from "@/services/API.js";
import Vue from "vue";
import {
  FormWrapper,
  FormControl,
  FormLabel,
  FormInput,
  FormHelper,
  FormError,
} from "@/components/ui/forms";
import ChannelMultiselect from "@/components/watchmen/channels/ChannelMultiselect";
import SelectList from "@/components/ui/fields/SelectList";
import UiBadge from "@/components/ui/Badge.vue";
import { 
  UiCompletionCard,
  UiCompletionStepper,
  UiCompletionLine,
  UiCompletionList 
} from "@/components/ui/completion";

import { check_rules } from "./check-rules";

export default {
  name: "MonitoringForm",
  components: {
    FormWrapper,
    FormControl,
    FormLabel,
    FormInput,
    FormHelper,
    FormError,
    ChannelMultiselect,
    SelectList,
    UiBadge,
    UiCompletionCard,
    UiCompletionStepper,
    UiCompletionLine,
    UiCompletionList,
},
  props: {
    value: {
      type: Object,
    },
  },
  data() {
    return {
      model: this.value,
      channelsRules: this.check_rules(this.value.channels),
      availableDevices: [],
    };
  },
  computed: {
    messages() {
      return {
        lock: {
          item: "$t('remoteMonitoring.form.tooltipDeviceDisabled')",
          folder: "$t('remoteMonitoring.form.tooltipGroupDisabled')",
        },
      };
    },
  },
  methods: {
    check_rules,
    isDatiSelected(dati) {
      return !!this.model?.id && dati?.rmprocedures[0]?.id === this.model.id
    },
    isRmOfDatiWaiting(dati) {
      const pivot = dati?.rmprocedures[0]?.pivot;
      return pivot?.status == 5 && !pivot?.status_reason
    },
    isDatiNumberMissing(dati) {
      // If dati is selected, dont disabled it to allow unselecting it. Else disable it to prevent selecting it
      return !this.isDatiSelected(dati) && !dati?.slot1PhoneNumber && !dati?.slot2PhoneNumber
    },
    loadAllDevices: function () {
      axios
        .get(
          API.endpoints.datis.indexByGroup+API.constructQueryParams({perpage: 9999999999999})+'&onlyTs=true'
        )
        .then((response) => {
          const availableDevicesList = response.data.reduce((acc, dati) => {
            if (dati.children != null) {
              dati.children.forEach((child) => {
                Vue.util.defineReactive(child, "selected", false);
              });
              acc.push({
                ...dati,
                children: dati.children.map((childDati) => ({
                  ...childDati,
                  selected: this.isDatiSelected(childDati),
                  disabled: this.isRmOfDatiWaiting(childDati) || this.isDatiNumberMissing(childDati),
                })),
              });
            } else {
              Vue.util.defineReactive(dati, "selected", false);
              acc.push({
                ...dati,
                selected: this.isDatiSelected(dati),
                disabled: this.isRmOfDatiWaiting(dati) || this.isDatiNumberMissing(dati),
              });
            }
            return acc;
          }, []);
          this.availableDevices = availableDevicesList
        });
        
    },
  },
  watch: {
    model: {
      handler: function (currentValue) {
        this.$emit("input", currentValue);
        this.channelsRules = this.check_rules(currentValue.channels)
      },
      deep: true
    },
    availableDevices: {
      handler: function (val) {
        var datis = []
        for (var key in val) {
          var dati = val[key]
          if (dati.children != null) {
            for (var ckey in dati.children) {
              var datiC = dati.children[ckey]
              if (datiC.selected) {
                datis.push(datiC.id)
              }
            }
          }
          else {
            if (dati.selected) {
              datis.push(dati.id)
            }
          }
        }
        this.model.datis = datis
      },
      deep: true,
    }
  },
  mounted() {
    this.loadAllDevices();
  },
};
</script>
<template>
  <div class="row">
    <div class="col-6">
      <FormWrapper>
        <FormControl>
          <FormLabel for="name">
            {{ $t('remoteMonitoring.procedures.form.name') }}
          </FormLabel>
          <FormInput id="name" v-model="model.name" />
        </FormControl>

        <UiCompletionCard>
          <div class="d-flex align-items-center mb-2 gap-2">
            <UiCompletionStepper 
              :steps="channelsRules.stepper.steps"
              :current="channelsRules.stepper.current"
              :status="channelsRules.stepper.status"
            />
            <strong class="ml-2">
              <span
                v-if="channelsRules.stepper.status === 'danger'"
                v-text="$t('remoteMonitoring.procedures.completion.status_danger')"
              ></span>
              <span
                v-if="channelsRules.stepper.status === 'warning'"
                v-text="$t('remoteMonitoring.procedures.completion.status_warning')"
              ></span>
              <span
                v-if="channelsRules.stepper.status === 'success'"
                v-text="$t('remoteMonitoring.procedures.completion.status_success')"
              ></span>
            </strong>
          </div>
          <UiCompletionList>
            <UiCompletionLine 
              :is_completed="channelsRules.hasMobile" 
              :is_required="channelsRules.hasChannels"
            >
              {{ $t('remoteMonitoring.procedures.completion.has_mobile') }}
            </UiCompletionLine>
            <UiCompletionLine
              :is_completed="channelsRules.hasLengthMin"
            >
              {{ $t('remoteMonitoring.procedures.completion.has_length_min') }}
            </UiCompletionLine>
            <UiCompletionLine
              :is_completed="channelsRules.hasLengthMinRecommended"
            >
              {{ $t('remoteMonitoring.procedures.completion.has_length_min_reco') }}
            </UiCompletionLine>
          </UiCompletionList>
        </UiCompletionCard>

        <FormControl>
          <FormLabel for="channels">
            {{ $t('remoteMonitoring.procedures.form.contacts') }}
            <UiBadge class="text-mono ml-2 rounded-full" :type="model.channels.length ? 'secondary' :  'danger'">
              {{ model.channels.length }}
            </UiBadge>
          </FormLabel>
          <FormHelper
            v-html="$t('remoteMonitoring.procedures.form.contacts_info')"
          />
          <ChannelMultiselect
            v-model="model.channels"
            channelType="phone,mobile"
            :maxChannels="4"
            :channelValidated="true"
            class="multiselect-with-index"
          />
          <FormError 
            v-if="channelsRules.hasUniquesInfo === false"
            v-text="$t('remoteMonitoring.procedures.form.contacts_errors_duplicate')"
          />
        </FormControl>

      </FormWrapper>
    </div>

    <div class="col-6">
      <FormWrapper>
        <FormControl>
          <FormLabel>
            {{$t('remoteMonitoring.procedures.form.devices')}}
            <UiBadge class="text-mono ml-2 rounded-full" :type="model.datis.length ? 'secondary' :  'danger'">
              {{ model.datis.length }}
            </UiBadge>
          </FormLabel>
          <FormHelper
            v-html="$t('remoteMonitoring.procedures.form.devices_info')"
          />
          <SelectList
            :list="availableDevices"
            itemType="datiMonitoring"
            :messages="messages"
          />
        </FormControl>
      </FormWrapper>
    </div>
  </div>
</template>
